import { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import { alpha, useTheme, styled } from '@mui/material/styles';
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Tooltip,
} from '@mui/material';
import Iconify from './Iconify';

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.body2,
    height: 48,
    position: 'relative',
    textTransform: 'capitalize',
    color: theme.palette.text.secondary,
    borderRadius: theme.shape.borderRadius,
  })
);

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  color: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
  collapsed: PropTypes.bool,
};

function NavItem({ item, active, collapsed }) {
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => setOpen((prev) => !prev);

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium',
  };

  // Parent with children
  if (children) {
    return (
      <>
        <Tooltip title={collapsed ? title : ''} placement="right">
          <ListItemStyle
            onClick={handleOpen}
            sx={{
              ...(isActiveRoot && activeRootStyle),
              px: collapsed ? 0 : 2.5,
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIconStyle sx={{ minWidth: 0, mr: collapsed ? 0 : 2, justifyContent: 'center' }}>
              {icon}
            </ListItemIconStyle>
            {!collapsed && <ListItemText disableTypography primary={title} />}
            {info && !collapsed && info}
            {!collapsed && (
              <Iconify
                icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
                sx={{ width: 16, height: 16, ml: 1 }}
              />
            )}
          </ListItemStyle>
        </Tooltip>

        <Collapse in={open && !collapsed} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((child) => {
              const { title, path } = child;
              const isActiveSub = active(path);

              return (
                <Tooltip key={title} title={collapsed ? title : ''} placement="right">
                  <ListItemStyle
                    component={RouterLink}
                    to={path}
                    sx={{
                      ...(isActiveSub && activeSubStyle),
                      px: collapsed ? 0 : 4,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                    }}
                  >
                    <ListItemIconStyle sx={{ minWidth: 0, mr: collapsed ? 0 : 2, justifyContent: 'center' }}>
                      <Box
                        component="span"
                        sx={{
                          width: 4,
                          height: 4,
                          display: 'flex',
                          borderRadius: '50%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'text.disabled',
                          transition: (theme) => theme.transitions.create('transform'),
                          ...(isActiveSub && { transform: 'scale(2)', bgcolor: 'primary.main' }),
                        }}
                      />
                    </ListItemIconStyle>
                    {!collapsed && <ListItemText disableTypography primary={title} />}
                  </ListItemStyle>
                </Tooltip>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  // Parent without children
  return (
    <Tooltip title={collapsed ? title : ''} placement="right">
      <ListItemStyle
        component={RouterLink}
        to={path}
        sx={{
          ...(isActiveRoot && activeRootStyle),
          px: collapsed ? 0 : 2.5,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <ListItemIconStyle sx={{ minWidth: 0, mr: collapsed ? 0 : 2, justifyContent: 'center' }}>
          {icon}
        </ListItemIconStyle>
        {!collapsed && <ListItemText disableTypography primary={title} />}
        {info && !collapsed && info}
      </ListItemStyle>
    </Tooltip>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
  collapsed: PropTypes.bool,
};

export default function NavSection({ navConfig, collapsed = false, ...other }) {
  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {navConfig.map((item) => (
          <NavItem key={item.title} item={item} active={match} collapsed={collapsed} />
        ))}
      </List>
    </Box>
  );
}

